import React from 'react';
import { Empty, Table } from 'antd';
import { RootState } from '../../../../store/rootReducer';
import { useSelector } from 'react-redux';
import { Referrals } from '../redux/types';
import { Modal, Collapse } from 'antd';
import { MdKeyboardArrowDown, MdKeyboardArrowRight } from 'react-icons/md';
import useWindowDimensions from '../../../common/useWindowDimensions';

const { Panel } = Collapse;

type Props = {
    loading: boolean;
    isTableOpen: boolean;
    setIsTableOpen: (isOpen: boolean) => void;
    userStatus: string;
};
const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const createDateFormat = (d: string) => {
    if (d) {
        let date = new Date(d); //record.Referral.creationAudit.updatedAt
        return `${date.getDate()}-${months[date.getMonth()]}-${date.getFullYear()}`;
    }
    return '';
};

const ReferralTable: React.FC<Props> = ({ loading, isTableOpen, setIsTableOpen, userStatus }) => {
    const { width } = useWindowDimensions();
    const referralsData = useSelector((state: RootState) => state.referral.referrals);

    const data = referralsData && referralsData.referrals;

    const selectStatusColor = (status: string) => {
        switch (status) {
            case 'Activated':
                return 'referral_background_active';
            case 'Successful Referral':
                return 'referral_background_success';
            case 'Fee Disbursed':
                return 'referral_background_fee_disbursed';
            case 'Expired':
                return 'referral_background_expired';
            default:
                return '';
        }
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: '',
            render: (record: Referrals) => <span>{record?.refereeUser.name}</span>,
        },
        {
            title: 'Email',
            dataIndex: '',
            ellipsis: true,
            render: (record: Referrals) => <span>{record.refereeUser.email || '-'}</span>,
        },
        {
            title: 'Referral Date',
            dataIndex: '',
            render: (record: Referrals) => (
                <span>{createDateFormat(record.refereeUser.creationAudit.updatedAt) || '-'}</span>
            ),
        },
        {
            title: 'Expiry Date',
            dataIndex: '',
            render: (record: Referrals) => <span>{createDateFormat(record.expireAt) || '-'}</span>,
        },
        {
            title: 'Status',
            dataIndex: '',
            render: (record: Referrals) => (
                <div className="flex items-center">
                    <div className={`h-2 w-2 rounded-full ${selectStatusColor(record.status)} mr-1`} />{' '}
                    <span className="text-xs not-italic font-normal">{record.status || '-'}</span>
                </div>
            ),
        },
    ];

    if (userStatus !== 'Approved') {
        columns.pop();
    }

    return (
        <div>
            <Modal
                className="modal_table_block md:my-2 md:mx-auto md:h-auto"
                width={width > 730 ? '80%' : '100%'}
                title="REFERRAL DETAILS"
                centered={true}
                footer={null}
                open={isTableOpen}
                onCancel={() => setIsTableOpen(false)}
            >
                <div className="hidden w-full md:flex">
                    <Table
                        rowClassName={'table-row-dark'}
                        columns={columns}
                        dataSource={data}
                        className="w-full custome_scroller"
                        rowKey={(record) => `${record.refereeUser.name}_${record.uuid}`}
                        pagination={false}
                        scroll={{ y: 400 }}
                        loading={loading}
                    />
                </div>
                <div className="w-full md:hidden">
                    {data ? (
                        data.map((record: Referrals, i: number) => (
                            <Collapse
                                key={i + 1}
                                bordered={false}
                                defaultActiveKey={['1']}
                                expandIconPosition={'right'}
                                className="mt-2 rounded-xl bg-white"
                                expandIcon={({ isActive }) =>
                                    isActive ? (
                                        <MdKeyboardArrowDown size={'1.5rem'} />
                                    ) : (
                                        <MdKeyboardArrowRight size={'1.5rem'} />
                                    )
                                }
                            >
                                <Panel
                                    header={
                                        <p className="text-base not-italic font-bold">{record?.refereeUser.name}</p>
                                    }
                                    key={1}
                                >
                                    <div className="flex">
                                        <div className="w-2/4">
                                            <p className="text-xs not-italic font-bold">Email</p>
                                            <p className="text-xs not-italic font-normal">
                                                {record.refereeUser.email || '-'}
                                            </p>
                                        </div>
                                        <div className="w-2/4 pl-4">
                                            <p className="text-xs not-italic font-bold">Referral Date</p>
                                            <p className="text-xs not-italic font-normal">
                                                {createDateFormat(record.refereeUser.creationAudit.updatedAt) || '-'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex justify-between pt-4">
                                        {userStatus === 'Approved' && (
                                            <div>
                                                <p className="text-xs not-italic font-bold">Status</p>
                                                <div className="flex items-center">
                                                    <div
                                                        className={`h-2 w-2 rounded-full ${selectStatusColor(
                                                            record.status,
                                                        )} mr-1`}
                                                    />{' '}
                                                    <span className="text-xs not-italic font-normal">
                                                        {record.status || '-'}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                        <div className={`w-2/4 ${userStatus === 'Approved' ? 'pl-4' : ''}`}>
                                            <p className="text-xs not-italic font-bold">Expiry Date</p>
                                            <p className="text-xs not-italic font-normal">
                                                {createDateFormat(record.expireAt) || '-'}
                                            </p>
                                        </div>
                                    </div>
                                </Panel>
                            </Collapse>
                        ))
                    ) : (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={<p className="font-semibold text-lg text-gray-500">No data available</p>}
                        />
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default ReferralTable;
